import React, { useEffect, useState } from 'react';
import { Link } from '@/components/general/Link/Link';
import { CenteredContentWrapper } from '@/components/layout/CenteredContentWrapper/CenteredContentWrapper';
import { RadioField } from '@/components/forms/inputs/RadioField/RadioField';
import { ErrorAlert } from '@/components/forms/alerts/ErrorAlert/ErrorAlert';
import { Alert, Button, Grid, Typography } from '@mui/material';
import { useConfigStore } from '@/store/configStore/configStore';
import { useAuthStore } from '@/store/authStore/authStore';
import { Method2fa } from '@/types/enums/auth/Method2fa';
import { LOGIN_PHRASES } from '@/consts/phrases/loginPhrases';
import { LOGIN_ERROR_PHRASES } from '@/consts/phrases/errorPhrases';
import { useForm } from 'react-hook-form';
import { useRouter } from '@/hooks';
import { useRedirectUrl } from '@/hooks/general/useRedirectUrl';
import { getRedirectURLFromStatus } from '@/utils/auth/getRedirectURLFromStatus';
import { usePhrase } from '@/hooks/general/usePhrase/usePhrase';
import { FetchErrorsType } from '@/types';
import { use2faMethods } from '@/hooks/queries/auth';
import { TwoFactorMethodFormSkeleton } from './TwoFactorMethodFormSkeleton';
import { useExtractedFetchErrorMessage } from '@/hooks/general/useExtractedFetchErrorMessage';

import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';

import styles from 'styles/modules/Auth.module.css';
interface TwoFactorMethodFormValues {
    method: Method2fa;
}

export const TwoFactorMethodForm = () => {
    const router = useRouter();
    const redirectUrl = useRedirectUrl();
    const { t } = usePhrase();
    const { website_2fa_email_resend_code_timeout } = useConfigStore((state) => ({
        website_2fa_email_resend_code_timeout: state.website_2fa_email_resend_code_timeout,
    }));
    const { data: methods, isLoading: isLoadingMethods, error: methodsError } = use2faMethods();
    const methodsErrorMessage = useExtractedFetchErrorMessage(methodsError);
    const { method, sendMethod2fa } = useAuthStore((state) => ({
        method: state.code2fa.method,
        sendMethod2fa: state.sendMethod2fa,
    }));
    const { control, handleSubmit } = useForm<TwoFactorMethodFormValues>({
        values: {
            method: method ?? Method2fa.Email,
        },
    });

    const [isSendMethodLoading, setIsSendMethodLoading] = useState<boolean>(false);
    const [onSendError, setOnSendError] = useState<FetchErrorsType>();
    const [wasCode2faSentRecently, setWasCode2faSentRecently] = useState<boolean>(false);

    const isLoading = isLoadingMethods || isSendMethodLoading;

    const hasNoMethods = !methods?.length;
    const hasOnlyOneMethod = methods?.length === 1;

    const onSubmit = async ({ method }: TwoFactorMethodFormValues) => {
        setIsSendMethodLoading(true);
        const res = await sendMethod2fa(method, website_2fa_email_resend_code_timeout);
        setIsSendMethodLoading(false);
        if (res.type === 'success') {
            router.push(getRedirectURLFromStatus('2fa-submit', redirectUrl));
        } else {
            setOnSendError(res.error);
        }
    };

    useEffect(() => {
        const errorPhraseId = onSendError?.generalErrors?.[0]?.phraseId;
        const hasBeenSentRecently = errorPhraseId === LOGIN_ERROR_PHRASES.CODE_SENT_RECENTLY;
        if (hasBeenSentRecently) {
            setWasCode2faSentRecently(true);
        }
    }, [onSendError]);

    useRedirectToSubmitIfHasOneMethod(hasOnlyOneMethod, router);

    const methodLabelMap = {
        [Method2fa.Email]: LOGIN_PHRASES.BY_EMAIL,
        [Method2fa.Sms]: LOGIN_PHRASES.BY_SMS,
        [Method2fa.Totp]: LOGIN_PHRASES.BY_TOTP,
    };
    const getMethodLabel = (method: Method2fa) => methodLabelMap[method] || '';
    const methodsData = methods
        ? methods.map((m) => ({
              label: String(t(getMethodLabel(m))),
              value: m,
          }))
        : [];

    const onBackButtonClicked = () => {
        setWasCode2faSentRecently(false);
        setOnSendError(undefined);
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} className={styles.auth}>
            <CenteredContentWrapper
                navigationContent={
                    wasCode2faSentRecently ? (
                        <Button
                            variant="text"
                            size="small"
                            startIcon={<KeyboardBackspaceIcon />}
                            onClick={onBackButtonClicked}
                        >
                            {t(LOGIN_PHRASES.BACK)}
                        </Button>
                    ) : null
                }
            >
                {isLoadingMethods || hasOnlyOneMethod ? (
                    <TwoFactorMethodFormSkeleton />
                ) : (
                    <>
                        <Typography variant="h2" align="center" className={styles.title}>
                            {t(LOGIN_PHRASES.VERIFY)}
                        </Typography>
                        {methodsError && (
                            <ErrorAlert
                                id="methods_error_message_box"
                                errorMessage={methodsErrorMessage}
                                sx={{ mb: 2 }}
                            />
                        )}
                        {hasNoMethods ? (
                            <Alert severity="error" id="general_error_message_box">
                                {t(LOGIN_ERROR_PHRASES.SOMETHING_WRONG_RETURN_TO_LOGIN)}
                            </Alert>
                        ) : (
                            <Grid container spacing={1}>
                                {onSendError && (
                                    <Grid size={12} className={styles.errorBlock}>
                                        <ErrorAlert
                                            id="message_box"
                                            errorMessage={onSendError}
                                            sx={{ mb: 1 }}
                                        />
                                    </Grid>
                                )}
                                {!wasCode2faSentRecently && (
                                    <>
                                        <Grid size={12}>
                                            <p>{t(LOGIN_PHRASES.UNUSUAL_ACTIVITY)}</p>
                                        </Grid>
                                        <Grid size={12}>
                                            <RadioField<{ method: Method2fa }>
                                                control={control}
                                                name="method"
                                                data-testid="method-input"
                                                data={methodsData}
                                            />
                                        </Grid>
                                    </>
                                )}
                                <Grid size={12} sx={{ mt: 2 }}>
                                    {!wasCode2faSentRecently ? (
                                        <Button
                                            sx={{ width: '100%' }}
                                            id="get-code"
                                            type="submit"
                                            loading={isLoading}
                                            variant="contained"
                                            disabled={isLoading}
                                        >
                                            {t(LOGIN_PHRASES.NEXT)}
                                        </Button>
                                    ) : (
                                        <Link href="/login/2fa/submit" passHref>
                                            <Button
                                                sx={{ width: '100%' }}
                                                variant="contained"
                                                type="button"
                                            >
                                                {t(LOGIN_PHRASES.ENTER_OTC)}
                                            </Button>
                                        </Link>
                                    )}
                                </Grid>
                            </Grid>
                        )}
                    </>
                )}
            </CenteredContentWrapper>
        </form>
    );
};

const useRedirectToSubmitIfHasOneMethod = (
    hasOneMethod: boolean,
    router: ReturnType<typeof useRouter>,
) => {
    const [isRedirected, setIsRedirected] = useState(false);
    useEffect(() => {
        if (hasOneMethod && !isRedirected) {
            router.push('/login/2fa/submit');
            setIsRedirected(true);
        }
    }, [router, hasOneMethod, isRedirected]);
};
